import { useRef, useState, useEffect } from 'react';
import { logEvent } from '@commerce/traffic2-utils';
import type { VideoConfig, VideoStep } from '../../types/video';
import { PulsatingButton } from '../PulsatingButton';
import { TerminalFrame } from '../TerminalFrame';  
import { Tooltip, ButtonTooltip } from '../Tooltip';
import { 
  getButtonConfig, 
  getButtonTooltipConfig, 
  getTooltipConfig,
  hasButton,
  hasTooltip,
  hasButtonTooltip
} from '../Utils/stepHelpers';
import '@ux/text/styles';
import { setVideoSpeed } from './Utils/setVideoSpeed';
import { isPortraitOrientation } from '../Utils/isPortraitOrientation';

interface Props {
  config: VideoConfig;
}

export function VideoSimulation({ config }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);  
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentStep, setCurrentStep] = useState<VideoStep | undefined>(
    config.steps.find(step => step.id === config.initialStepId)!
  );
  const [showButton, setShowButton] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showButtonTooltip, setShowButtonTooltip] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);
  const [opportunityId, setOpportunityId] = useState('');

  const resetStates = () => {
    setShowButton(false);
    setShowTooltip(false);
    setShowButtonTooltip(false);
    setIsButtonClicked(false);
  };

  // Reset to initial state
  const resetToInitialState = () => {
    const initialStep = config.steps.find(step => step.id === config.initialStepId)!;
    setCurrentStep(initialStep);
    resetStates();
    
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(console.error);
    }
  };

  useEffect(() => {
    // Detect if we're in an iframe
    setIsInIframe(window.parent !== window);

    // Read opportunityId from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const opportunityIdFromUrl = urlParams.get('opportunityId');
    if (opportunityIdFromUrl) {
      setOpportunityId(opportunityIdFromUrl);
    }

    if (!videoRef.current) return;
  
    // Handle video loading and notify a parent of the iframe, 
    // that the first frame was loaded and a spinner can be hidden
    // in case we use the app as a content of an iframe in an external page
    const handleFirstFrameLoaded = () => {
      // Track the demo started event
      if (opportunityIdFromUrl) {
        logEvent({
          eid: `commerce.interactive-demo.${config.type}.demo_started`,
          type: "track",
          props: {
            opportunityId: opportunityIdFromUrl || '',
          },
        });
      }
      
      // Notify parent iframe that video is loaded and ready    
      if (window.parent && window.parent !== window) {
        const message = {
          type: 'IFRAME_VIDEO_LOADED',
          source: config.type
        };
        window.parent.postMessage(message, '*');
        // Log the message to the console so the parent of the iframe can see it
        // and use it to hide the spinner etc.
        if (process.env.NODE_ENV === 'development') {
          console.log('First video frame loaded', message);
        }
      }
    };

    // Initial video play when component mounts
    videoRef.current.play().catch(console.error);

    // Handle the first frame loaded event
    videoRef.current.addEventListener('loadeddata', handleFirstFrameLoaded);
    
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('loadeddata', handleFirstFrameLoaded);
      }
    };
  }, []); 

  // Set up video ended event handler
  useEffect(() => {
    if (!videoRef.current) return;
    
    const handleVideoEnded = () => {
      resetToInitialState();
    };
    
    videoRef.current.addEventListener('ended', handleVideoEnded);
    
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('ended', handleVideoEnded);
      }
    };
  }, [config.initialStepId]);

  // Set up postMessage communication for external controls
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // In production, you should validate event.origin for security
      if (process.env.NODE_ENV === 'development') {
        console.log('Received message from parent:', event.data);
      }

      const { type, data } = event.data || {};

      switch (type) {
        case 'EXTERNAL_NAVIGATE_NEXT':
          goToNextStep();
          break;
        case 'EXTERNAL_NAVIGATE_PREVIOUS':
          goToPreviousStep();
          break;
        case 'EXTERNAL_NAVIGATE_TO_STEP':
          if (data?.stepId) {
            goToStep(data.stepId);
          }
          break;
        case 'EXTERNAL_GET_STEP_INFO':
          if (currentStep) {
            notifyStepChange(currentStep);
          }
          break;
        case 'EXTERNAL_GET_ALL_STEPS':
          notifyAllSteps();
          break;
        default:
          break;
      }
    };

    if (isInIframe) {
      window.addEventListener('message', handleMessage);
      
      // Send initial step information to parent
      if (currentStep) {
        notifyStepChange(currentStep);
      }
    }

    return () => {
      if (isInIframe) {
        window.removeEventListener('message', handleMessage);
      }
    };
  }, [isInIframe, currentStep]);

  const switchToNextStep = () => {
    if (!videoRef.current || !currentStep) return;

    const nextStep = config.steps.find(step => step.id === currentStep.nextStepId);
   
     // Reset states for the new step
    resetStates();
    
    if (!nextStep) {
      return; // Don't change currentStep if there's no next step
    };

    setCurrentStep(nextStep);
  };

  // Manual navigation -> Go to the next step
  const goToNextStep = () => {
    if (!currentStep) return;
    
    const currentIndex = config.steps.findIndex(step => step.id === currentStep.id);
    // Find the next step that has a button
    const nextStepWithButton = config.steps.slice(currentIndex + 1).find(step => hasButton(step));
    
    if (nextStepWithButton) {
      setCurrentStep(nextStepWithButton);
      resetStates();
      
      if (videoRef.current) {
        videoRef.current.currentTime = nextStepWithButton.time.start;
      }
      
      // Notify parent if in iframe
      if (isInIframe) {
        notifyStepChange(nextStepWithButton);
      }
      
      // Track manual step navigation
      trackNavigationEvent('next', currentStep.id, nextStepWithButton);
    }
  };

  // Manual navigation -> Go to the previous step
  const goToPreviousStep = () => {
    if (!currentStep) return;
    
    const currentIndex = config.steps.findIndex(step => step.id === currentStep.id);
    // Find the previous step that has a button (iterate backwards)
    const previousSteps = config.steps.slice(0, currentIndex).reverse();
    const prevStepWithButton = previousSteps.find(step => hasButton(step));
    
    if (prevStepWithButton) {
      setCurrentStep(prevStepWithButton);
      resetStates();
      
      if (videoRef.current) {
        videoRef.current.currentTime = prevStepWithButton.time.start;
      }
      
      // Notify parent if in iframe
      if (isInIframe) {
        notifyStepChange(prevStepWithButton);
      }
      
      // Track manual step navigation
      trackNavigationEvent('previous', currentStep.id, prevStepWithButton);
    }
  };

  // Manual navigation -> Go to a specific step
  const goToStep = (stepId: string) => {
    const targetStep = config.steps.find(step => step.id === stepId);
    // Only navigate if the target step has a button
    if (!targetStep || !hasButton(targetStep) || !videoRef.current) return;
    
    setCurrentStep(targetStep);
    resetStates();
    videoRef.current.currentTime = targetStep.time.start;
    
    // Notify parent if in iframe
    if (isInIframe) {
      notifyStepChange(targetStep);
    }
    
    // Track step jump
    trackNavigationEvent('jump', currentStep?.id || '', targetStep);
  };

  // Format time for display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Communicate with parent iframe
  const notifyParent = (messageType: string, data: any = {}) => {
    if (window.parent && window.parent !== window) {
      const message = {
        type: messageType,
        source: config.type,
        ...data
      };
      window.parent.postMessage(message, '*');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Sent message to parent:', message);
      }
    }
  };

  // Send step information to parent
  const notifyStepChange = (step: VideoStep) => {
    const stepsWithButtons = config.steps.filter(s => hasButton(s));
    const indexInButtonsOnly = stepsWithButtons.findIndex(s => s.id === step.id);
    
    notifyParent('IFRAME_STEP_CHANGED', {
      currentStep: {
        id: step.id,
        index: indexInButtonsOnly,
        title: step?.title || step?.tooltip?.text || step?.button?.tooltip?.text || step.id,
        description: step?.description || '',
        timeStart: step.time.start,
        timeEnd: step.time.end,
        formattedTime: `${formatTime(step.time.start)} - ${formatTime(step.time.end)}`
      },
      totalSteps: stepsWithButtons.length,
      isFirstStep: indexInButtonsOnly === 0,
      isLastStep: indexInButtonsOnly === stepsWithButtons.length - 1,
    });
  };

  // Send all steps information to parent (for building external timeline)
  // Only include steps that have buttons
  const notifyAllSteps = () => {
    let stepsWithButtons = config.steps.filter(step => hasButton(step));
    
    // TODO: This is a temporary solution to only include the steps that are relevant for the Smart Terminal Duo demo
    // Due to long code review, Production approval process and urgent app necessity
    // Remove when the steps will be properly adjusted in the
    // mwcs-frontend/**/smart-terminal-duo-demo/StepsPopup.tsx file
    const stepsForSmartTerminalDuo = ['step2', 'step9', 'step13'];
    stepsWithButtons = stepsWithButtons.filter(({ id }) => stepsForSmartTerminalDuo.includes(id));

    notifyParent('IFRAME_ALL_STEPS', {
      videoType: config.type,
      totalSteps: stepsWithButtons.length,
      aspectRatio: config.aspectRatio,
      allSteps: stepsWithButtons.map((step, index) => ({
        id: step.id,
        index,
        title: step?.title || step?.tooltip?.text || step?.button?.tooltip?.text || step.id,
        description: step?.description || '',
        timeStart: step.time.start,
        timeEnd: step.time.end,
        formattedTime: `${formatTime(step.time.start)} - ${formatTime(step.time.end)}`,
        hasTooltip: hasTooltip(step),
        speed: step.speed || 1
      }))
    });
  };

  const trackEvent = (
    videoType: string,
    { id: stepId, tooltip, button, time }: VideoStep,
    eventType: string
  ) => {
    const eid = `commerce.interactive-demo.${videoType}.step_button.${eventType}`;
    logEvent({
      eid,
      type: "click",
      props: {
        stepId,
        tooltip: tooltip?.text || button?.tooltip?.text || "",
        timeStart: time?.start || 0,
        timeEnd: time?.end || 0,
        opportunityId,
      },
    });
  };

  const trackNavigationEvent = (
    navigationType: 'next' | 'previous' | 'jump',
    fromStepId: string,
    toStep: VideoStep
  ) => {
    const eid = `commerce.interactive-demo.${config.type}.manual_navigation.${navigationType}`;
    logEvent({
      eid,
      type: "click",
      props: {
        fromStepId,
        stepId: toStep.id,
        tooltip: toStep.tooltip?.text || toStep.button?.tooltip?.text || "",
        timeStart: toStep.time?.start || 0,
        timeEnd: toStep.time?.end || 0,
        opportunityId,
      },
    });
  };

  useEffect(() => {
    if (!videoRef.current || !currentStep) return;

    const handleTimeUpdate = () => {
      if (!videoRef.current || !currentStep) return;
      
      const currentTime = videoRef.current.currentTime;
      setVideoSpeed(videoRef, currentStep?.speed || 1);

      if (process.env.NODE_ENV === 'development') {
        console.log('Current time:', currentTime);
        console.log('Current step:', currentStep);
      }
      /**
       * Threshold is used for reliable cross-browser timestamp detection:
       * 1. timeupdate events fire at different frequencies across browsers:
       *    - Chrome/Edge: ~4 updates/second (may skip exact timestamps)
       *    - Firefox: ~15-20 updates/second
       *    - Safari: ~10 updates/second
       * 2. Frame boundaries affect exact timestamps (e.g., in 30fps video, frames
       *    are at 0.000s, 0.033s, 0.067s, etc.) and browsers may round differently
       * 3. Without a threshold, we risk missing the exact timestamp completely
       *    if timeupdate jumps from 4.9s to 5.1s, skipping our 5.0s target
       */
      const threshold = 0.1 * (currentStep.speed || 1); // Threshold for time comparison

      if ((currentTime + threshold >= currentStep.time.start) 
        && (currentTime < currentStep.time.end)) {
        
        // Show button if step has button configuration
        if (hasButton(currentStep) && !isButtonClicked) {
          videoRef.current.pause();
          trackEvent(config.type, currentStep, "pause");
          setShowButton(true);
        }
        
        // Show tooltip if step has tooltip configuration
        if (hasTooltip(currentStep) && !showTooltip) {
          setShowTooltip(true);
        }
        
        // Show button tooltip if step has button tooltip configuration
        if (hasButtonTooltip(currentStep) && !showButtonTooltip) {
          setShowButtonTooltip(true);
        }
        
        return;
       }     
       
       // This is to avoid the time before the first step being switch to the next step
       // but to switch to the next step if the current time is greater than or equal to the current step's end time
      else if (currentTime >= currentStep.time.end) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Switching to next step, current time:', currentTime, 'current step:', currentStep);
        }
        trackEvent(config.type, currentStep, "switch");
        switchToNextStep();
      }
    };

    videoRef.current.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener("timeupdate", handleTimeUpdate);
      }
    };
  }, [config.steps, currentStep, isButtonClicked, showButton, showTooltip, showButtonTooltip]);

  const handleStepClick = () => {
    if (!videoRef.current || !currentStep) return;

    trackEvent(config.type, currentStep, "click");
    videoRef.current.play().catch(console.error);
    trackEvent(config.type, currentStep, "resume");
    setIsButtonClicked(true);
    // Hide button and button tooltip after click
    setShowButton(false);
    setShowButtonTooltip(false);
  };

  const renderButton = () => {
    if (!showButton || !currentStep) return null;
    
    const buttonConfig = getButtonConfig(currentStep);
    if (!buttonConfig) return null;
    
    return (
        <PulsatingButton
          ref={buttonRef}
          position={buttonConfig.position}
          size={buttonConfig.size || '30%'}
          onClick={handleStepClick}
        />
    );
  };

  const renderButtonTooltip = () => {
    if (!showButtonTooltip || !currentStep) return null;
    
    const tooltipConfig = getButtonTooltipConfig(currentStep);
    if (!tooltipConfig) return null;
    
    return (
      <ButtonTooltip
        ref={buttonRef}
        text={tooltipConfig.text}
        placement={tooltipConfig.placement}
        variant={tooltipConfig.variant}
        containerRef={containerRef}
      />
    );
  };


  const renderTooltip = () => {
    if (!showTooltip || !currentStep) return null;
    
    const tooltipConfig = getTooltipConfig(currentStep);
    if (!tooltipConfig) return null;
    
    return (
      <Tooltip
        text={tooltipConfig.text}
        position={tooltipConfig.position}
        variant={tooltipConfig.variant}
        containerRef={containerRef}
      />
    );
  };

  const renderStepNavigation = () => {
    if (!currentStep) return null;
    
    const currentIndex = config.steps.findIndex(step => step.id === currentStep.id);
    const isFirstStep = currentIndex === 0;
    const isLastStep = currentIndex === config.steps.length - 1;
    
    return (
      <div className="flex items-center justify-center gap-4 mt-4">
        <button
          onClick={goToPreviousStep}
          disabled={isFirstStep}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            isFirstStep
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700'
          }`}
        >
          ← Previous Step
        </button>
        
        <span className="text-sm text-gray-600 px-4">
          Step {currentIndex + 1} of {config.steps.length}
        </span>
        
        <button
          onClick={goToNextStep}
          disabled={isLastStep}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            isLastStep
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700'
          }`}
        >
          Next Step →
        </button>
      </div>
    );
  };

  const renderTimestampBlock = () => {
    return (
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Video Steps Timeline</h3>
        <div className="space-y-2">
          {config.steps.map((step, index) => {
            const isCurrentStep = currentStep?.id === step.id;
            const stepNumber = index + 1;
            
            return (
              <button
                key={step.id}
                onClick={() => goToStep(step.id)}
                className={`w-full text-left p-3 rounded-lg transition-all ${
                  isCurrentStep
                    ? 'bg-blue-100 border-2 border-blue-500 text-blue-800'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      isCurrentStep
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {stepNumber}
                    </span>
                    <div>
                      <div className="font-medium">
                        {typeof step.tooltip === 'string' 
                          ? step.tooltip 
                          : step.tooltip?.text || `Step ${stepNumber}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatTime(step.time.start)} - {formatTime(step.time.end)}
                      </div>
                    </div>
                  </div>
                  {isCurrentStep && (
                    <div className="text-blue-500">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

const isPortrait = isPortraitOrientation(config.aspectRatio);
  
  const containerClass = isPortrait 
    ? "relative max-w-md mx-auto"  // Narrower for portrait
    : "relative max-w-4xl mx-auto"; // Wide for landscape

  return (
    <div className={containerClass} ref={containerRef}>
      <TerminalFrame aspectRatio={config.aspectRatio}>
          <video
            ref={videoRef}
            className="w-full h-full object-cover rounded-lg"
            controls={false}
            playsInline
            muted
            autoPlay
            preload="auto"
            // !!! IMPORTANT: The video should be in the same origin as the page
            crossOrigin="anonymous"
          >
            <source src={config.videoUrl} type="video/mp4" />
            <source src={config.videoUrl} type="video/webm" />
            <source src={config.videoUrl} type="video/ogg" />
            <p>Your browser does not support the video tag.</p>
          </video>
        {renderButton()}
        {renderTooltip()}
        {renderButtonTooltip()}
      </TerminalFrame>
      
      {
      process.env.NODE_ENV === 'development' && (
        <>
          {/* Just for local development and testing */}
          {/* Step Navigation Controls - Only show when NOT in iframe */}
          {!isInIframe && renderStepNavigation()}
          {/* Timestamp Block - Only show when NOT in iframe */}
          {!isInIframe && renderTimestampBlock()}
        </>
      )}
      </div>
  );
}
